import React, { useEffect, useMemo, useState } from 'react';
import { createOperatingRoom, updateOperatingRoom } from '../services/api.ts';
import type { Hospital, OperatingRoom, OperatingRoomUpdate } from '../services/api.ts';
import { useCredentials } from '../data/queries.ts';
import Modal from './Modal.tsx';
import { CUSTOM_PRESET, ROOM_PRESETS, nextRoomNames, presetLabel } from './roomNaming.ts';

// Create rooms at a hospital, or edit one room, in a dialog.
//
// Creating is a bulk add: pick a room type (or a custom name) and how many, and the rooms are numbered
// on from the ones already there (roomNaming.ts). Editing covers every field of the room except its
// hospital — the backend cannot move a room between hospitals.
//
// The cardiac and C-section types are tri-state, like the backend: "Not set" leaves the room to the
// backend's fallback of reading each case's surgery type (backend/src/utils/room-classification.ts).

type TriState = 'unset' | 'yes' | 'no';
const triFrom = (v: boolean | undefined): TriState => (v === undefined ? 'unset' : v ? 'yes' : 'no');
const triTo = (v: TriState): boolean | null => (v === 'unset' ? null : v === 'yes');

export interface RoomFormValues {
  hospitalId: string;
  /** Create only: a preset room type, or "Custom…" to use `customName`. */
  preset: string;
  customName: string;
  count: string;
  /** Edit only: the room's name. */
  name: string;
  credential: string;
  isHazard: boolean;
  isDoctorAssignableOnly: boolean;
  hasIonizingRadiation: boolean;
  isCardiacRoom: TriState;
  isCSectionRoom: TriState;
}

export const emptyRoomForm = (hospitalId = ''): RoomFormValues => ({
  hospitalId, preset: 'Generic OR', customName: '', count: '1', name: '',
  credential: '', isHazard: false, isDoctorAssignableOnly: false, hasIonizingRadiation: false,
  isCardiacRoom: 'unset', isCSectionRoom: 'unset',
});

export const roomFormFromRoom = (room: OperatingRoom): RoomFormValues => ({
  ...emptyRoomForm(room.hospitalId),
  name: room.name,
  credential: room.credential ?? '',
  isHazard: !!room.isHazard,
  isDoctorAssignableOnly: !!room.isDoctorAssignableOnly,
  hasIonizingRadiation: !!room.hasIonizingRadiation,
  isCardiacRoom: triFrom(room.isCardiacRoom),
  isCSectionRoom: triFrom(room.isCSectionRoom),
});

/** The PUT body for an edit. Rooms are named by their number, so a rename sets both, as it always has. */
export const roomUpdateFromForm = (form: RoomFormValues): OperatingRoomUpdate => ({
  name: form.name.trim(),
  roomNumber: form.name.trim(),
  credential: form.credential,
  isHazard: form.isHazard,
  isDoctorAssignableOnly: form.isDoctorAssignableOnly,
  hasIonizingRadiation: form.hasIonizingRadiation,
  isCardiacRoom: triTo(form.isCardiacRoom),
  isCSectionRoom: triTo(form.isCSectionRoom),
});

interface RoomFormDialogProps {
  open: boolean;
  /** The room being edited, or null to create rooms. */
  room: OperatingRoom | null;
  /** Create only: the hospital to preselect (e.g. from that hospital's "+ Add room"). */
  defaultHospitalId?: string;
  hospitals: Hospital[];
  /** Every room, so new rooms are numbered on from the ones a hospital already has. */
  allRooms: OperatingRoom[];
  onClose: () => void;
  onSaved: (message: string) => void;
}

const FORM_ID = 'room-form';
const hint: React.CSSProperties = { display: 'block', marginTop: '4px', color: 'var(--text-muted)' };

const RoomFormDialog: React.FC<RoomFormDialogProps> = ({ open, room, defaultHospitalId, hospitals, allRooms, onClose, onSaved }) => {
  const editing = !!room;
  const initial = useMemo(
    () => (room ? roomFormFromRoom(room) : emptyRoomForm(defaultHospitalId ?? (hospitals.length === 1 ? hospitals[0].hospitalId : ''))),
    // Only re-seed when the target changes, not each time the hospital list refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [room, defaultHospitalId]
  );
  const [form, setForm] = useState<RoomFormValues>(initial);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const credentialsQuery = useCredentials();
  const credentialOptions = credentialsQuery.data ?? [];

  useEffect(() => {
    if (open) {
      setForm(initial);
      setErrorMessage(null);
    }
  }, [open, initial]);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const set = <K extends keyof RoomFormValues>(key: K, value: RoomFormValues[K]) => setForm(f => ({ ...f, [key]: value }));
  const hospitalName = (id: string) => hospitals.find(h => h.hospitalId === id)?.name ?? '';

  const create = async () => {
    const label = presetLabel(form.preset, form.customName);
    if (!form.hospitalId) throw new Error('Choose a hospital.');
    if (!label) throw new Error('Enter a name for the custom room type.');
    const count = Number(form.count);
    if (!Number.isInteger(count) || count < 1) throw new Error('Enter how many rooms (1 or more).');
    const existing = allRooms.filter(r => r.hospitalId === form.hospitalId).map(r => r.name);
    const names = nextRoomNames(label, count, existing);
    const isCardiacRoom = triTo(form.isCardiacRoom);
    const isCSectionRoom = triTo(form.isCSectionRoom);
    for (const name of names) {
      await createOperatingRoom({
        roomNumber: name,
        name,
        hospitalId: form.hospitalId,
        ...(form.credential && { credential: form.credential }),
        ...(form.isHazard && { isHazard: true }),
        ...(form.isDoctorAssignableOnly && { isDoctorAssignableOnly: true }),
        ...(form.hasIonizingRadiation && { hasIonizingRadiation: true }),
        ...(isCardiacRoom !== null && { isCardiacRoom }),
        ...(isCSectionRoom !== null && { isCSectionRoom }),
      });
    }
    onSaved(`Added ${names.length} room${names.length > 1 ? 's' : ''} to ${hospitalName(form.hospitalId)}: ${names.join(', ')}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      if (editing && room) {
        if (!form.name.trim()) throw new Error('Room name cannot be empty.');
        await updateOperatingRoom(room.operatingRoomId, roomUpdateFromForm(form));
        onSaved(`${form.name.trim()} updated.`);
      } else {
        await create();
      }
      onClose();
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error || error?.message || `Failed to ${editing ? 'update' : 'add'} room`);
    } finally {
      setSaving(false);
    }
  };

  const checkbox = (key: 'isHazard' | 'isDoctorAssignableOnly' | 'hasIonizingRadiation', label: string, help: string) => (
    <div className="form-group">
      <label className="checkbox-label">
        <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} />
        <span className="checkbox-text">{label}</span>
      </label>
      <small style={hint}>{help}</small>
    </div>
  );

  const triSelect = (key: 'isCardiacRoom' | 'isCSectionRoom', id: string, label: string, help: string) => (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <select id={id} className="form-control" value={form[key]} onChange={e => set(key, e.target.value as TriState)} style={{ maxWidth: '320px' }}>
        <option value="unset">Not set (guess from case names)</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
      <small style={hint}>{help}</small>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={() => { if (!saving) onClose(); }}
      title={editing ? 'Edit room' : 'Add rooms'}
      closeOnBackdrop={!dirty && !saving}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form={FORM_ID} className="btn btn-primary" disabled={saving}>
            {saving ? (editing ? 'Saving…' : 'Adding…') : (editing ? 'Save changes' : 'Add rooms')}
          </button>
        </>
      }
    >
      {errorMessage && <div className="alert alert-error" role="alert">{errorMessage}</div>}
      <form id={FORM_ID} onSubmit={handleSubmit}>
        {editing ? (
          <>
            <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>At {hospitalName(form.hospitalId)}</p>
            <div className="form-group">
              <label htmlFor="room-name">Room Name</label>
              <input type="text" id="room-name" value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label htmlFor="room-hospital">Hospital</label>
              <select id="room-hospital" className="form-control" value={form.hospitalId} onChange={e => set('hospitalId', e.target.value)} required>
                <option value="">Select a hospital</option>
                {hospitals.map(h => <option key={h.hospitalId} value={h.hospitalId}>{h.name}</option>)}
              </select>
            </div>
            <div className="grid grid-2">
              <div className="form-group">
                <label htmlFor="room-type">Room Type</label>
                <select id="room-type" className="form-control" value={form.preset} onChange={e => set('preset', e.target.value)}>
                  {ROOM_PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="room-count">How Many</label>
                <input type="number" id="room-count" min="1" value={form.count} onChange={e => set('count', e.target.value)} />
              </div>
            </div>
            {form.preset === CUSTOM_PRESET && (
              <div className="form-group">
                <label htmlFor="room-custom-name">Custom Room Name</label>
                <input type="text" id="room-custom-name" value={form.customName} onChange={e => set('customName', e.target.value)} placeholder="e.g. Hybrid" />
              </div>
            )}
            <small style={{ ...hint, marginTop: '-8px', marginBottom: '16px' }}>
              “Endo” × 3 → Endo 1, Endo 2, Endo 3, numbered on from any already there. A single MRI/CT gets no number.
            </small>
          </>
        )}

        <div className="form-group">
          <label htmlFor="room-credential">Required Credential</label>
          <select id="room-credential" className="form-control" value={form.credential} onChange={e => set('credential', e.target.value)}>
            <option value="">No credential required</option>
            {credentialOptions.map(c => <option key={c} value={c}>{c}</option>)}
            {/* Keep a stored credential selectable even if it is no longer in the list. */}
            {form.credential && !credentialOptions.includes(form.credential) && (
              <option value={form.credential}>{form.credential}</option>
            )}
          </select>
          {credentialsQuery.isError && <small style={{ ...hint, color: 'var(--danger-text)' }}>Failed to load credentials</small>}
        </div>

        {checkbox('isHazard', 'Hazardous Operating Room', 'Marks the room as hazardous.')}
        {checkbox('isDoctorAssignableOnly', 'Doctor Assignable Only', 'Only doctors can be assigned (no CRNAs or students).')}
        {checkbox('hasIonizingRadiation', 'Ionizing Radiation Room',
          'Pregnant CRNAs are never allocated here. Use this for rooms whose cases don’t already flag radiation (fluoroscopy, C-arm, cath/EP lab, ERCP…).')}

        {/* ROOM TYPE — the source of truth for Symphony's cardiac reservation and OB carve-out. */}
        {triSelect('isCardiacRoom', 'room-cardiac', 'Cardiac Room',
          'Yes: only a cardiac anesthesiologist may staff this room, including in the final coverage pass.')}
        {triSelect('isCSectionRoom', 'room-csection', 'C-Section (OB) Room',
          'Yes: the room gets a reserved CRNA and joins the site’s dedicated OB anesthesiologist. Set this for rooms named “C/S” — the case-name fallback does not recognise that form.')}
      </form>
    </Modal>
  );
};

export default RoomFormDialog;
